import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Phone, Mail, MapPin, Clock, User, Calendar, CheckCircle, Star, ArrowRight, CreditCard, Shield, Users, Award, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import UniversalFormWrapper from '../components/UniversalFormWrapper';

interface PaidConsultationFormData {
  name: string;
  email: string;
  phone: string;
  course: string;
  consultationType: string;
  preferredDate: string;
  preferredTime: string;
  message: string;
}

const PaidConsultantPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<PaidConsultationFormData>();

  const onSubmit = (data: PaidConsultationFormData) => {
    const subject = 'Expert Consultation Request';
    const body =
      `Full Name: ${data.name}\n` +
      `Email: ${data.email}\n` +
      `Phone: ${data.phone}\n` +
      `Course of Interest: ${data.course}\n` +
      `Consultation Package: ${data.consultationType}\n` +
      `Preferred Date: ${data.preferredDate}\n` +
      `Preferred Time: ${data.preferredTime}\n` +
      `Message: ${data.message || ''}`;
    const mailtoLink = `mailto:infonammasewa@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };

  const paidServices = [
    {
      title: "SILVER MEMBERSHIP",
      price: "FREE",
      description: "Basic guidance package for students.",
      icon: <User className="h-8 w-8" />,
      features: [
        "FULL GUIDANCE (BASICS)"
      ]
    },
    {
      title: "GOLD MEMBERSHIP",
      price: "Rs. 2,000",
      description: "Engineering and paramedical counseling support.",
      icon: <Calendar className="h-8 w-8" />,
      features: [
        "SUPPORT CHOICE FILLING (ENGINEERING, PARAMEDICAL)",
        "COUNSELLING APPLICATION SUPPORT",
        "ALL COLLEGE FEES DETAILS"
      ]
    },
    {
      title: "PLATINUM MEMBERSHIP",
      price: "Rs. 5,000",
      description: "Medical and veterinary counseling support.",
      icon: <Star className="h-8 w-8" />,
      features: [
        "SUPPORT FOR CHOICE FILLING (MBBS, BDS, VETERINARY)",
        "COUNSELLING APPLICATION SUPPORT",
        "ALL COLLEGE FEES"
      ]
    },
    {
      title: "DIAMOND MEMBERSHIP",
      price: "Rs. 7,500",
      description: "Comprehensive support with placement training.",
      icon: <Award className="h-8 w-8" />,
      features: [
        "SUPPORT FOR CHOICE FILLING",
        "ALL APPLICATION PROCESS",
        "ALL COLLEGE FEES",
        "PART TIME INCOME OPPORTUNITY",
        "PLACEMENTS TRAINING (WORTH 10,000)"
      ]
    },
    {
      title: "PREMIUM MEMBERSHIP",
      price: "Rs. 10,000/Yr",
      description: "All-inclusive package with extended benefits.",
      icon: <Shield className="h-8 w-8" />,
      features: [
        "ALL DIAMOND MEMBERSHIP BENEFITS",
        "OFF CAMPUS PLACEMENTS ASSISTANCE",
        "INTERVIEW STUDY MATERIALS (TNPSC, Bank Exams)",
        "Ph.D GUIDANCE (For 4 Year)"
      ]
    }
  ];

  const whyChoosePaid = [
    {
      icon: <Shield className="h-12 w-12" />,
      title: "Guaranteed Results",
      description: "100% success rate in securing admissions with our premium consultants"
    },
    {
      icon: <Users className="h-12 w-12" />,
      title: "Expert Consultants",
      description: "Top-tier consultants with 15+ years of experience in their specializations"
    },
    {
      icon: <CreditCard className="h-12 w-12" />,
      title: "Comprehensive Support",
      description: "End-to-end support from career planning to successful admission"
    },
    {
      icon: <Award className="h-12 w-12" />,
      title: "Premium Resources",
      description: "Access to exclusive resources, templates, and insider knowledge"
    }
  ];

  return (
    <UniversalFormWrapper 
      formType="consultation" 
      source="PaidConsultantPage" 
      priority="high"
    >
      <div className={`min-h-screen relative font-[Inter,Poppins,sans-serif] antialiased bg-gradient-to-br from-[#0a0a1f] via-[#14142b] to-[#1e1e2f] text-white`}>
      {/* Back to Home Button */}
      <div className="absolute top-4 left-4 z-10">
        <Link to="/about-us" className="flex items-center text-cyan-300 hover:text-pink-400 font-semibold text-lg transition-colors">
          <ArrowLeft className="mr-2 h-5 w-5" /> Back to Consultancy
        </Link>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0a0a1f] via-[#14142b] to-[#1e1e2f] text-white py-16 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className={`text-5xl md:text-6xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight glow-text drop-shadow-glow`}>
              Expert Consultation Services
            </h1>
            <p className={`text-2xl md:text-3xl mb-8 text-cyan-200 glow-text drop-shadow-glow`}>
              Get exclusive access to top education consultants and guaranteed results
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#consultation-packages"
                className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:ring-2 hover:ring-purple-400 transition inline-flex items-center justify-center backdrop-blur-lg border border-purple-400/30 shadow-md"
              >
                View Packages
                <ArrowRight className="ml-2 h-5 w-5 text-purple-300" />
              </a>
              <a
                href="#expert-form"
                className="bg-cyan-400/10 text-pink-400 px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition inline-flex items-center justify-center backdrop-blur-lg border border-cyan-400/10 shadow-md"
              >
                Book Now
                <ArrowRight className="ml-2 h-5 w-5 text-pink-400" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Premium Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className={`text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight glow-text drop-shadow-glow`}>
              Why Choose Expert Consultation?
            </h2>
            <p className={`text-lg max-w-2xl mx-auto text-cyan-200 glow-text drop-shadow-glow`}>
              Invest in your future with our expert services and professional guidance
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoosePaid.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="backdrop-blur-lg bg-cyan-400/5 border border-cyan-400/10 rounded-xl shadow-md p-6 hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition text-center"
              >
                <div className={`mb-4 text-cyan-400 flex justify-center drop-shadow-glow`}>
                  {item.icon}
                </div>
                <h3 className={`text-xl font-semibold mb-3 text-cyan-100 glow-text drop-shadow-glow`}>
                  {item.title}
                </h3>
                <p className={`text-cyan-200 glow-text drop-shadow-glow`}>
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Packages */}
      <section id="consultation-packages" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className={`text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight glow-text drop-shadow-glow`}>
              Expert Consultation Packages
            </h2>
            <p className={`text-lg max-w-2xl mx-auto text-cyan-200 glow-text drop-shadow-glow`}>
              Choose the package that best fits your needs and goals
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {paidServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="backdrop-blur-lg bg-cyan-400/5 border border-cyan-400/10 rounded-xl shadow-md p-6 hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition relative"
              >
                {index === 2 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className={`mb-4 text-cyan-400 drop-shadow-glow`}>
                  {service.icon}
                </div>
                <h3 className={`text-xl font-semibold mb-4 text-cyan-100 glow-text drop-shadow-glow`}>
                  {service.title}
                </h3>
                <div className="mb-4">
                  <span className={`text-lg text-cyan-200 font-bold`}>
                    {service.price}
                  </span>
                </div>
                <p className={`text-cyan-200 mb-6 glow-text drop-shadow-glow`}>
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-cyan-200">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href="#expert-form"
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-4 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors font-medium text-center block"
                >
                  Select Package
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Consultation Form */}
      <section id="expert-form" className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className={`text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight glow-text drop-shadow-glow`}>
              Book Expert Consultation
            </h2>
            <p className={`text-lg max-w-2xl mx-auto text-cyan-200 glow-text drop-shadow-glow`}>
              Fill out the form below and secure your expert consultation session
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold mb-6 text-pink-400">Expert Support</h3>
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-purple-400" />
                  <span className="text-pink-400">+91 98765 43210 (Expert Line)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-purple-400" />
                  <span className="text-pink-400">expert@educonsult.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-purple-400" />
                  <span className="text-pink-400">SEWA Head Office, Manickampalayam, Tiruchengode, Namakkal, Tamil Nadu , India</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-purple-400" />
                  <span className="text-pink-400">24/7 Expert Support</span>
                </div>
              </div>

              <div className="backdrop-blur-lg bg-purple-400/5 border border-purple-400/20 rounded-xl p-6">
                <h4 className="font-semibold mb-4 text-purple-400">Expert Benefits</h4>
                <ul className="space-y-2 text-sm text-cyan-200">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    Priority booking and scheduling
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    Dedicated expert consultant
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    Follow-up support included
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    Satisfaction guarantee
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Premium Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="backdrop-blur-lg bg-gradient-to-br from-purple-400/5 to-pink-400/5 border border-purple-400/20 rounded-xl shadow-md p-8"
            >
              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-cyan-100 mb-2">
                    Expert Consultation Booked!
                  </h3>
                  <p className="text-cyan-200">
                    Our expert team will contact you within 2 hours to confirm your session.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Reminder for file attachments */}
                  <div className="mb-6 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 rounded">
                    <strong>Note:</strong> After clicking submit, your email client will open with your consultation details.<br />Please manually attach any required documents before sending the email.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-cyan-200 mb-1">
                        Full Name *
                      </label>
                      <input
                        {...register('name', { required: 'Name is required' })}
                        type="text"
                        className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
                        placeholder="Enter your full name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-cyan-200 mb-1">
                        Email Address *
                      </label>
                      <input
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address'
                          }
                        })}
                        type="email"
                        className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
                        placeholder="Enter your email"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-1">
                      Phone Number *
                    </label>
                    <input
                      {...register('phone', { required: 'Phone number is required' })}
                      type="tel"
                      className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
                      placeholder="Enter your phone number"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-cyan-200 mb-1">
                        Course of Interest *
                      </label>
                      <select
                        {...register('course', { required: 'Please select a course' })}
                        className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                      >
                        <option value="" className="bg-gray-800">Select a course</option>
                        <option value="mbbs" className="bg-gray-800">MBBS</option>
                        <option value="engineering" className="bg-gray-800">Engineering</option>
                        <option value="scholarships" className="bg-gray-800">Scholarships</option>
                        <option value="international" className="bg-gray-800">International Studies</option>
                        <option value="other" className="bg-gray-800">Other</option>
                      </select>
                      {errors.course && (
                        <p className="mt-1 text-sm text-red-400">{errors.course.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-cyan-200 mb-1">
                        Consultation Package *
                      </label>
                      <select
                        {...register('consultationType', { required: 'Please select a package' })}
                        className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                      >
                        <option value="" className="bg-gray-800">Select package</option>
                        <option value="silver" className="bg-gray-800">Silver Membership (FREE)</option>
                        <option value="gold" className="bg-gray-800">Gold Membership (Rs. 2,000)</option>
                        <option value="platinum" className="bg-gray-800">Platinum Membership (Rs. 5,000)</option>
                        <option value="diamond" className="bg-gray-800">Diamond Membership (Rs. 7,500)</option>
                        <option value="premium" className="bg-gray-800">Premium Membership (Rs. 10,000/Yr)</option>
                      </select>
                      {errors.consultationType && (
                        <p className="mt-1 text-sm text-red-400">{errors.consultationType.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-cyan-200 mb-1">
                        Preferred Date *
                      </label>
                      <input
                        {...register('preferredDate', { required: 'Please select a date' })}
                        type="date"
                        className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                      />
                      {errors.preferredDate && (
                        <p className="mt-1 text-sm text-red-400">{errors.preferredDate.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-cyan-200 mb-1">
                        Preferred Time *
                      </label>
                      <select
                        {...register('preferredTime', { required: 'Please select a time' })}
                        className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                      >
                        <option value="" className="bg-gray-800">Select time</option>
                        <option value="09:00" className="bg-gray-800">9:00 AM</option>
                        <option value="11:00" className="bg-gray-800">11:00 AM</option>
                        <option value="14:00" className="bg-gray-800">2:00 PM</option>
                        <option value="16:00" className="bg-gray-800">4:00 PM</option>
                        <option value="18:00" className="bg-gray-800">6:00 PM</option>
                      </select>
                      {errors.preferredTime && (
                        <p className="mt-1 text-sm text-red-400">{errors.preferredTime.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-cyan-200 mb-1">
                      Additional Requirements
                    </label>
                    <textarea
                      {...register('message')}
                      rows={4}
                      className="w-full px-3 py-2 bg-white/10 border border-cyan-400/30 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-400"
                      placeholder="Tell us about your specific requirements and goals..."
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-4 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors font-medium"
                  >
                    Book Expert Consultation
                  </motion.button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
    </UniversalFormWrapper>
  );
};

export default PaidConsultantPage;
